import { attributes } from '../../core/ids-attributes';
import IdsDataSource, { PAGINATION_TYPES } from '../../core/ids-data-source';

import '../../components/ids-pager/ids-pager';
import '../../components/ids-button/ids-button';
import '../../components/ids-menu-button/ids-menu-button';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';
import { IdsConstructor } from '../../core/ids-element';

import type IdsPager from '../../components/ids-pager/ids-pager';
import type { PaginationTypes } from '../../core/ids-data-source';

interface PaginationHandler {
  onPagingReload?(reloadEventType: string): void;
}

interface AutoFitHandler {
  autoFit?: boolean;
  autoFitSet?: boolean;
  redraw?(): void;
}

type Constraints = IdsConstructor<EventsMixinInterface & PaginationHandler & AutoFitHandler>;

/**
 * A mixin that adds pager functionality to components
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsPagerMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  /** Reference to the user-provided IdsPager component */
  #pager: IdsPager | undefined;

  /** Store original pageSize when pagination is toggled off */
  #savedPageSize: number = 0;

  /**
   * Gets the internal IdsDataSource object
   * @returns {IdsDataSource} object
   */
  datasource = new IdsDataSource();

  constructor(...args: any[]) {
    super(...args);
  }

  /**
   * Set the internal IdsPager component
   * @param {HTMLElement} pager The pager dom element
   */
  set pager(pager: IdsPager | undefined) {
    if (this.#pager && this.#pager !== pager) {
      this.offEvent('pagenumberchange', this.#pager);
      this.offEvent('pagesizechange', this.#pager);
    }

    this.#pager = pager;

    const pageNumber = Math.max(this.pageNumber || 1, 1);
    const pageSize = Math.max(this.pageSize || 0, 1);

    this.datasource.pageSize = pageSize;
    if (!this.pager) return;
    if (this.pagination !== PAGINATION_TYPES.STANDALONE) this.pager.innerHTML = this.pagerTemplate();
    this.pager.total = this.datasource.total;
    this.pager.pageNumber = pageNumber;
    this.pager.pageSize = pageSize;

    this.#attachEventListeners();
  }

  /**
   * Get the internal IdsPager component
   * @returns {IdsPager | undefined} the pager dom element
   */
  get pager(): IdsPager | undefined {
    if (!this.pagination || this.pagination === PAGINATION_TYPES.NONE) {
      return undefined;
    }
    return this.#pager || (this.querySelector('ids-pager') as IdsPager | null) || undefined;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @private
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...(superclass as unknown as { attributes: string[] }).attributes,
      attributes.PAGINATION,
      attributes.PAGE_NUMBER,
      attributes.PAGE_SIZE,
      attributes.PAGE_TOTAL,
    ];
  }

  /**
   * Handle Setting changes of the value has changed by calling the getter
   * in the extending class.
   * @param {string} name The property name
   * @param {string} oldValue The property old value
   * @param {string} newValue The property new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (oldValue === newValue) {
      return;
    }

    const shouldReload = [
      attributes.PAGE_NUMBER,
      attributes.PAGE_SIZE,
      attributes.PAGE_TOTAL,
      attributes.PAGINATION,
    ].includes(name);

    if (shouldReload && typeof this.onPagingReload === 'function') {
      this.onPagingReload(name);
    }

    // Re-attach or remove pager when pagination attribute changes
    if (name === attributes.PAGINATION) {
      this.#attachPager();
    }
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   * @private
   */
  connectedCallback() {
    super.connectedCallback?.();
    this.#attachPager();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.offEvent('pagenumberchange', this.pager);
    this.offEvent('pagesizechange', this.pager);
    this.#pager = undefined;
  }

  pagerTemplate() {
    const pageSize = Math.max(this.pageSize || 0, 1);
    return `
      <ids-pager-button first></ids-pager-button>
      <ids-pager-button previous></ids-pager-button>
      <ids-pager-input></ids-pager-input>
      <ids-pager-button next></ids-pager-button>
      <ids-pager-button last></ids-pager-button>
      <ids-pager-dropdown slot="end" page-size="${pageSize}"></ids-pager-dropdown>
    `;
  }

  /**
   * @param {number} pageNumber - a page number to start with
   * @param {number} pageSize - number of items to return
   * @returns {Array} the paginated data
   * @see IdsDataSource.paginate()
   */
  paginate(pageNumber = 1, pageSize = 10) {
    return this.datasource.paginate(pageNumber, pageSize);
  }

  /**
   * Sets the pagination attribute
   * @param {string} value - none|client-side|standalone
   */
  set pagination(value: PaginationTypes) {
    this.setAttribute(attributes.PAGINATION, value);
    this.datasource.pagination = value;
    // Clear pageSize when pagination is disabled to show all records
    if (!value || value === PAGINATION_TYPES.NONE) {
      this.#savedPageSize = this.datasource.pageSize || 0;
      this.datasource.pageSize = 0;
    } else if (value && value !== PAGINATION_TYPES.STANDALONE) {
      // Restore saved pageSize when re-enabling pagination, or use the page-size
      // attribute, or fall back to the pager default (10)
      const attrPageSize = parseInt(this.getAttribute(attributes.PAGE_SIZE) || '', 10);
      this.datasource.pageSize = this.#savedPageSize || (attrPageSize > 0 ? attrPageSize : 10);
      // Reset to page 1 when re-enabling pagination
      this.pageNumber = 1;
    }
  }

  /**
   * Gets the pagination attribute
   * @returns {PaginationTypes} default is "none"
   */
  get pagination(): PaginationTypes {
    return this.getAttribute(attributes.PAGINATION) as PaginationTypes || PAGINATION_TYPES.NONE;
  }

  /**
   * Set the page-number attribute
   * @param {number} value - new the page-number
   */
  set pageNumber(value: number) {
    if (!this.pager) return;
    this.setAttribute(attributes.PAGE_NUMBER, String(value));
    this.datasource.pageNumber = Number(value);
    this.pager.pageNumber = Number(value);
    this.datasource.refreshPreviousState();
  }

  /**
   * Get the page-number attribute
   * @returns {number} - the current page-number
   */
  get pageNumber(): number { return parseInt(this.getAttribute(attributes.PAGE_NUMBER) || '') || this.pager?.pageNumber || 1; }

  /**
   * Set the page-size attribute
   * @param {number} value - new the page-size
   */
  set pageSize(value: number) {
    if (!this.pager) return;
    this.setAttribute(attributes.PAGE_SIZE, String(value));
    this.datasource.pageSize = Number(value);
    this.pager.pageSize = Number(value);
    this.datasource.refreshPreviousState();
  }

  /**
   * Get the page-size attribute
   * @returns {number} - the current page-size
   */
  get pageSize() { return parseInt(this.getAttribute(attributes.PAGE_SIZE) || '') || this.pager?.pageSize || 1; }

  /**
   * Set the page-total attribute
   * @param {number} value - new the page-total
   */
  set pageTotal(value) {
    if (!this.pager) return;
    this.setAttribute(attributes.PAGE_TOTAL, String(value));
    this.datasource.total = Number(value);
    this.pager.total = Number(value);
    this.datasource.refreshPreviousState();
  }

  /**
   * Get the page-total attribute
   * @returns {number} - the current page-total
   */
  get pageTotal(): number {
    return parseInt(this.getAttribute(attributes.PAGE_TOTAL) || '') || this.datasource.total;
  }

  /**
   * Appends IdsPager to this.shadowRoot if pagination is enabled.
   * @private
   */
  #attachPager() {
    if (!this.pagination || this.pagination === PAGINATION_TYPES.NONE) {
      this.#removePager();
      // Trigger auto-fit recalculation when pager is removed
      if (this.autoFit === true && this.autoFitSet === true) {
        this.autoFitSet = false;
        if (typeof this.redraw === 'function') {
          this.redraw();
        }
      }
      return;
    }

    if (this.pagination === PAGINATION_TYPES.STANDALONE) {
      return;
    }

    const nestedPager = this.querySelector('ids-pager') || this.shadowRoot?.querySelector('ids-pager');
    if (!nestedPager && !this.#pager) {
      // Create a new pager if one doesn't exist
      const newPager = document.createElement('ids-pager') as IdsPager;
      this.pager = newPager;

      // Only attach if container exists (component is rendered)
      if (this.pager && this.container) {
        this.container.after(this.pager);
        // Trigger auto-fit recalculation when pager is added
        if (this.autoFit === true && this.autoFitSet === true) {
          this.autoFitSet = false;
          if (typeof this.redraw === 'function') {
            this.redraw();
          }
        }
      }
    }
  }

  /**
   * Detaches and removes the pager from the DOM.
   * Resolves the element directly because the `pager` getter returns null
   * once pagination is 'none', which would otherwise leave the pager attached.
   * @private
   */
  #removePager() {
    const pager = this.#pager
      || this.shadowRoot?.querySelector('ids-pager')
      || this.querySelector('ids-pager');

    if (pager) {
      this.offEvent('pagenumberchange', pager);
      this.offEvent('pagesizechange', pager);
      pager.remove();
    }

    this.#pager = undefined;
  }

  #attachEventListeners() {
    this.offEvent('pagenumberchange', this.pager);
    this.onEvent('pagenumberchange', this.pager, (event: CustomEvent) => {
      this.pageNumber = Number(event.detail.value);
    });

    this.offEvent('pagesizechange', this.pager);
    this.onEvent('pagesizechange', this.pager, (event: CustomEvent) => {
      this.pageSize = Number(event.detail.value);
      this.pageNumber = 1;
    });
  }
};

export default IdsPagerMixin;
